import type { User } from 'features/auth';
import type { Program, ProgramContentObject, ProgramContentObjectItem } from '../../models';
import { isContentObjectAuthorProfile } from '../content-object';
import { hasMembershipInProgramProfile, isProgramAdminProfile, isProgramAuthorProfile } from '../user-roles';
import { isOrganizationAdminProfile, isOrganizationsAdmin, isProgramsAdmin, isSuperAdmin } from '../utils';

type SubscribableProgram = {
  id: string;
  mandatory: boolean;
  privacy: 'private' | 'public' | 'secret';
  requestedToJoin?: boolean;
  settings: { allowUnsubscribe: boolean };
  status: string;
};

export const canCreateProgramContent = ({ program, viewer }: { program: SubscribableProgram; viewer: User }) =>
  (isSuperAdmin({ viewer: viewer.railsUserData }) ||
    isProgramAdminProfile({ programId: program.id, viewer: viewer.railsUserData }) ||
    isProgramAuthorProfile({ programId: program.id, viewer: viewer.railsUserData })) &&
  program.status !== 'finished';

export const canViewAdminDashboardPrograms = ({ viewer }: { viewer: User }) =>
  isSuperAdmin({ viewer: viewer.railsUserData }) ||
  isOrganizationsAdmin({ viewer: viewer.railsUserData }) ||
  isProgramsAdmin({ viewer: viewer.railsUserData });

export const canRateProgramObjectProfile = ({
  contentObject,
  viewer,
}: {
  contentObject: ProgramContentObject;
  viewer: User;
}) => {
  const { organization, program } = contentObject;

  return Boolean(
    program.id &&
    !isSuperAdmin({ viewer: viewer.railsUserData }) &&
    !isProgramAdminProfile({ programId: program.id, viewer: viewer.railsUserData }) &&
    !isProgramAuthorProfile({ programId: program.id, viewer: viewer.railsUserData }) &&
    !isContentObjectAuthorProfile({ contentObject, viewer: viewer.railsUserData }) &&
    !isOrganizationAdminProfile({ organizationId: organization.id, viewer: viewer.railsUserData }),
  );
};

export const canViewRating = ({ contentObject, viewer }: { contentObject?: ProgramContentObject; viewer: User }) => {
  if (!contentObject) {
    return false;
  }

  return (
    Number(contentObject.evaluationRatingAverage) > 0 && canSeeFuturePosts({ program: contentObject.program, viewer })
  );
};

export const canViewProgramMembers = ({ program, viewer }: { program: Program; viewer: User }) =>
  Boolean(program.settings.viewMembers) || canManageProgram({ program, viewer });

export const canManageProgram = ({
  program,
  viewer: { railsUserData: viewer },
}: {
  program?: Program;
  viewer: User;
}) => {
  if (isSuperAdmin({ viewer })) {
    return true;
  }

  if (!program?.organization?.id) {
    return false;
  }

  return (
    isOrganizationAdminProfile({ organizationId: program.organization.id, viewer }) ||
    isProgramAdminProfile({ programId: program.id, viewer }) ||
    isProgramAuthorProfile({ programId: program.id, viewer })
  );
};

export const canDeleteProgramMember = ({ program, viewer }: { program: Program; viewer: User }) =>
  canManageProgram({ program, viewer });

export const canViewProgram = ({ program, viewer }: { program?: Program; viewer: User }) => {
  if (!program?.id) {
    return false;
  }

  return (
    (hasMembershipInProgramProfile({ programId: program.id, viewer: viewer.railsUserData }) &&
      program.status === 'published') ||
    canManageProgram({ program, viewer }) ||
    isSuperAdmin({ viewer: viewer.railsUserData })
  );
};

export const canInviteProgramMembers = ({ program, viewer }: { program: Program; viewer: User }) =>
  program.status === 'published' &&
  (canSeeFuturePosts({ program, viewer }) ||
    Boolean(
      program.organization &&
      isOrganizationAdminProfile({ organizationId: program.organization.id, viewer: viewer.railsUserData }),
    ));

export const canSeeFuturePosts = ({
  program,
  viewer,
}: {
  program?: Program | ProgramContentObjectItem;
  viewer: User;
}) =>
  Boolean(
    program?.id &&
    (isProgramAuthorProfile({ programId: program.id, viewer: viewer.railsUserData }) ||
      isSuperAdmin({ viewer: viewer.railsUserData }) ||
      isProgramAdminProfile({ programId: program.id, viewer: viewer.railsUserData })),
  );

export const canDownloadProgramReport = ({ program, viewer }: { program: ProgramContentObjectItem; viewer: User }) =>
  canSeeFuturePosts({ program, viewer });

export const hasJoinedProgram = ({ program, viewer }: { program: ProgramContentObjectItem; viewer: User }) =>
  hasMembershipInProgramProfile({ programId: program.id, viewer: viewer.railsUserData });

export const joinedAsUser = ({ program, viewer }: { program?: SubscribableProgram; viewer: User }) => {
  if (!program?.id) {
    return false;
  }

  return hasMembershipInProgramProfile({ programId: program.id, viewer: viewer.railsUserData });
};

export const hasUserSentJoinRequest = ({ program }: { program: SubscribableProgram; viewer: User }) =>
  program.privacy === 'private' && Boolean(program.requestedToJoin);

export const canUnsubscribe = ({ program, viewer }: { program: SubscribableProgram; viewer: User }) =>
  joinedAsUser({ program, viewer }) &&
  program.status === 'published' &&
  !program.mandatory &&
  program.settings.allowUnsubscribe;

export const isMemberOfProgram = ({ program, viewer }: { program?: SubscribableProgram; viewer: User }) =>
  Boolean(
    program?.id &&
    (hasMembershipInProgramProfile({ programId: program.id, viewer: viewer.railsUserData }) ||
      isProgramAdminProfile({ programId: program.id, viewer: viewer.railsUserData }) ||
      isProgramAuthorProfile({ programId: program.id, viewer: viewer.railsUserData })),
  );
